import { useEffect, useState } from "react";
import ClientAvatar from "@/components/ClientAvatar";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, RotateCcw, Instagram, CheckCircle, XCircle, Clock, Trash2, UserPlus, Loader2, Mail, Send } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { isTestAccount } from "@/lib/testAccounts";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
  booking_count_reset_at: string | null;
  instagram_link: string | null;
  instagram_approval_status: string | null;
  instagram_bypass_approval: boolean;
}

interface PendingClient {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  created_at: string;
  last_email_sent_at: string | null;
  expires_at: string;
}

const Clients = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [pending, setPending] = useState<PendingClient[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [newClient, setNewClient] = useState({ full_name: "", email: "", phone: "" });
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchData = async () => {
    const [profilesRes, bookingsRes, pendingRes] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("bookings").select("user_id, created_at, status").is("deleted_at", null),
      supabase.from("pending_clients").select("*").order("created_at", { ascending: false }),
    ]);

    setProfiles(((profilesRes.data as Profile[]) || []).filter((p) => !isTestAccount(p.user_id)));
    setBookings((bookingsRes.data || []).filter((b: any) => !isTestAccount(b.user_id)));
    setPending((pendingRes.data as PendingClient[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getBookingCount = (profile: Profile) => {
    return bookings.filter((b) => {
      if (b.user_id !== profile.user_id) return false;
      if (b.status === "cancelled") return false;
      if (profile.booking_count_reset_at && new Date(b.created_at) < new Date(profile.booking_count_reset_at)) return false;
      return true;
    }).length;
  };

  const getCancellationCount = (profile: Profile) => {
    return bookings.filter((b) => {
      if (b.user_id !== profile.user_id) return false;
      if (b.status !== "cancelled") return false;
      if (profile.booking_count_reset_at && new Date(b.created_at) < new Date(profile.booking_count_reset_at)) return false;
      return true;
    }).length;
  };

  const handleReset = async (userId: string) => {
    const { error } = await supabase
      .from("profiles")
      .update({ booking_count_reset_at: new Date().toISOString() })
      .eq("user_id", userId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Reset", description: "Booking count reset for this client" });
      setProfiles((prev) =>
        prev.map((p) =>
          p.user_id === userId ? { ...p, booking_count_reset_at: new Date().toISOString() } : p
        )
      );
    }
  };

  const handleDeleteClient = async (userId: string, name: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-auth", {
        body: { action: "delete_client", target_user_id: userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Deleted", description: `${name || "Client"} has been deleted` });
      setProfiles((prev) => prev.filter((p) => p.user_id !== userId));
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleAddClient = async () => {
    const name = newClient.full_name.trim();
    const emailTrim = newClient.email.trim();
    const phoneTrim = newClient.phone.trim();
    if (!name) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast({ title: "Error", description: "A valid email is required", variant: "destructive" });
      return;
    }
    if (!phoneTrim) {
      toast({ title: "Error", description: "Phone number is required", variant: "destructive" });
      return;
    }
    const digits = phoneTrim.replace(/\D/g, "");
    if (digits.length < 10) {
      toast({ title: "Error", description: "Phone number must be at least 10 digits", variant: "destructive" });
      return;
    }

    setAddLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-auth", {
        body: {
          action: "create_pending_client",
          email: emailTrim,
          full_name: name,
          phone: phoneTrim,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: "Invitation sent", description: `${name} will appear under Pending until they confirm via email.` });
      setNewClient({ full_name: "", email: "", phone: "" });
      setAddOpen(false);
      fetchData();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setAddLoading(false);
  };

  const handlePendingAction = async (
    action: "resend_pending_invite" | "confirm_pending_client" | "delete_pending_client",
    p: PendingClient
  ) => {
    setPendingActionId(p.id);
    try {
      const { data, error } = await supabase.functions.invoke("admin-auth", {
        body: { action, pending_id: p.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const msg =
        action === "resend_pending_invite" ? `Invitation re-sent to ${p.email}` :
        action === "confirm_pending_client" ? `${p.full_name} has been confirmed and added` :
        `${p.full_name} removed from pending`;
      toast({ title: "Done", description: msg });
      fetchData();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setPendingActionId(null);
  };


  // Merge confirmed profiles + pending clients into a single sortable, searchable list
  type Row =
    | { kind: "profile"; created_at: string; profile: Profile }
    | { kind: "pending"; created_at: string; pending: PendingClient };

  const allRows: Row[] = [
    ...profiles.map<Row>((p) => ({ kind: "profile", created_at: p.created_at, profile: p })),
    ...pending.map<Row>((p) => ({ kind: "pending", created_at: p.created_at, pending: p })),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const filtered = allRows.filter((row) => {
    const q = search.toLowerCase();
    if (row.kind === "profile") {
      const p = row.profile;
      return (
        (p.full_name || "").toLowerCase().includes(q) ||
        (p.email || "").toLowerCase().includes(q) ||
        (p.phone || "").toLowerCase().includes(q)
      );
    }
    const p = row.pending;
    return (
      p.full_name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      p.phone.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold italic">Clients</h1>
          <p className="text-muted-foreground text-sm mt-1">
            View all registered clients — <span className="text-primary">gold glow = 2 cancellations</span>, <span className="text-destructive">red = 3+ cancellations (blocked)</span>
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <UserPlus size={16} /> Add Client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite New Client</DialogTitle>
              <DialogDescription>
                The client will receive a confirmation email and only be added to your client list once they confirm — or you can confirm them manually from the Pending section.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="add-name">Full Name *</Label>
                <Input id="add-name" value={newClient.full_name} onChange={(e) => setNewClient(p => ({ ...p, full_name: e.target.value }))} placeholder="Jane Doe" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-email">Email *</Label>
                <Input id="add-email" type="email" value={newClient.email} onChange={(e) => setNewClient(p => ({ ...p, email: e.target.value }))} placeholder="jane@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-phone">Phone *</Label>
                <Input id="add-phone" type="tel" value={newClient.phone} onChange={(e) => setNewClient(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" />
              </div>
              <p className="text-xs text-muted-foreground">Name, email, and phone are all required. The account is only created once the client clicks the confirmation link in their email (or you confirm them manually).</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={handleAddClient} disabled={addLoading}>
                {addLoading ? <><Loader2 size={16} className="animate-spin mr-2" /> Sending…</> : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pending.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2">
              <Clock size={14} className="text-primary" />
              <span className="text-xs font-light tracking-[0.3em] uppercase text-muted-foreground">
                Pending Confirmation ({pending.length})
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Invited</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((p) => {
                  const expired = new Date(p.expires_at).getTime() < Date.now();
                  const busy = pendingActionId === p.id;
                  return (
                    <TableRow key={p.id} className={cn(expired && "opacity-60")}>
                      <TableCell className="font-medium text-sm">{p.full_name}</TableCell>
                      <TableCell className="text-sm">{p.email}</TableCell>
                      <TableCell className="text-sm">{p.phone}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(p.created_at).toLocaleDateString()}
                        {expired && <span className="ml-2 text-destructive">expired</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" disabled={busy}
                            onClick={() => handlePendingAction("resend_pending_invite", p)}>
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                            <span className="ml-1.5 hidden sm:inline">Resend</span>
                          </Button>
                          <Button size="sm" disabled={busy}
                            onClick={() => handlePendingAction("confirm_pending_client", p)}>
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                            <span className="ml-1.5 hidden sm:inline">Confirm</span>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" disabled={busy}>
                                <Trash2 size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove pending invitation?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {p.full_name} ({p.email}) will be removed from the pending list and the email link will stop working.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handlePendingAction("delete_pending_client", p)}>Remove</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search clients…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-card border-border/50"
        />
      </div>

      <Card className="bg-card border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-muted-foreground text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-muted-foreground text-sm">No clients found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Bookings</TableHead>
                  <TableHead>Cancellations</TableHead>
                  <TableHead>Instagram</TableHead>
                  <TableHead>IG Bypass</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => {
                  if (row.kind === "pending") {
                    const pc = row.pending;
                    const expired = new Date(pc.expires_at).getTime() < Date.now();
                    const busy = pendingActionId === pc.id;
                    return (
                      <TableRow key={`pending-${pc.id}`} className={cn("bg-primary/5", expired && "opacity-60")}>
                        <TableCell className="font-medium text-sm">
                          <div className="flex items-center gap-2.5">
                            <ClientAvatar userId={pc.id} name={pc.full_name} size="sm" />
                            <div className="flex items-center gap-2">
                              {pc.full_name}
                              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-light">
                                Pending
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-light inline-flex items-center gap-1">
                            <Clock size={10} /> Pending
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{pc.email}</TableCell>
                        <TableCell className="text-sm">{pc.phone}</TableCell>
                        <TableCell><span className="text-xs text-muted-foreground">—</span></TableCell>
                        <TableCell><span className="text-xs text-muted-foreground">—</span></TableCell>
                        <TableCell><span className="text-[10px] text-muted-foreground">—</span></TableCell>
                        <TableCell><span className="text-[10px] text-muted-foreground">—</span></TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(pc.created_at).toLocaleDateString()}
                          {expired && <div className="text-[10px] text-destructive">expired</div>}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" disabled={busy} className="h-8 px-2"
                              title="Resend invite"
                              onClick={() => handlePendingAction("resend_pending_invite", pc)}>
                              {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                            </Button>
                            <Button size="sm" disabled={busy} className="h-8 px-2"
                              title="Confirm now"
                              onClick={() => handlePendingAction("confirm_pending_client", pc)}>
                              {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0">
                                  <Trash2 size={14} />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove pending invitation?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {pc.full_name} ({pc.email}) will be removed from the pending list and the email link will stop working.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handlePendingAction("delete_pending_client", pc)}>Remove</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const p = row.profile;
                  const bookingCount = getBookingCount(p);
                  const cancellationCount = getCancellationCount(p);
                  const isWarning = cancellationCount === 2;
                  const isBlocked = cancellationCount >= 3;

                  return (
                    <TableRow
                      key={p.id}
                      className={cn(
                        "cursor-pointer",
                        isBlocked && "bg-destructive/10",
                        isWarning && "gold-glow-ring"
                      )}
                      onClick={() => navigate(`/admin/clients/${p.user_id}`)}
                    >
                      <TableCell className={cn("font-medium text-sm", isBlocked && "text-destructive")}>
                        <div className="flex items-center gap-2.5">
                          <ClientAvatar userId={p.user_id} name={p.full_name} size="sm" />
                          {p.full_name || "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 dark:text-green-400 font-light inline-flex items-center gap-1">
                          <CheckCircle size={10} /> Confirmed
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{p.email || "—"}</TableCell>
                      <TableCell className="text-sm">{p.phone || "—"}</TableCell>
                      <TableCell>
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          {bookingCount}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "text-xs font-bold px-2 py-1 rounded-full",
                            isBlocked
                              ? "bg-destructive/20 text-destructive"
                              : isWarning
                              ? "bg-primary/20 text-primary"
                              : "bg-muted text-muted-foreground"
                          )}
                        >
                          {cancellationCount}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(p.created_at).toLocaleDateString()}
                      </TableCell>
                      {/* Instagram status */}
                      <TableCell>
                        {p.instagram_link ? (
                          <div className="flex items-center gap-1.5">
                            {p.instagram_approval_status === "approved" ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 flex items-center gap-0.5">
                                <CheckCircle size={10} /> Approved
                              </span>
                            ) : p.instagram_approval_status === "pending" ? (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-700 flex items-center gap-0.5">
                                  <Clock size={10} /> Pending
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0 text-green-600"
                                  title="Approve"
                                   onClick={async (e) => {
                                    e.stopPropagation();
                                    // Approve: update status + generate coupon
                                    await supabase.from("profiles").update({ instagram_approval_status: "approved" }).eq("user_id", p.user_id);
                                    // Check if coupon exists
                                    const { data: existing } = await supabase.from("coupons").select("id").eq("user_id", p.user_id).eq("reason", "instagram_share").maybeSingle();
                                    if (!existing) {
                                      const code = "IG-" + Math.random().toString(36).substring(2, 8).toUpperCase();
                                      const igExpiry = new Date();
                                      igExpiry.setMonth(igExpiry.getMonth() + 6);
                                      await supabase.from("coupons").insert({ user_id: p.user_id, code, discount_amount: 5, reason: "instagram_share", expires_at: igExpiry.toISOString() });
                                      // Send credit approval email
                                      if (p.email) {
                                        supabase.functions.invoke("send-credit-email", {
                                          body: { email: p.email, client_name: p.full_name, coupon_code: code, discount_amount: 5, expires_at: igExpiry.toISOString(), reason: "instagram_share" },
                                        }).catch(() => {});
                                      }
                                    }
                                    toast({ title: "Approved", description: `Instagram link approved and $5 coupon generated for ${p.full_name || "client"}` });
                                    fetchData();
                                  }}
                                >
                                  <CheckCircle size={12} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0 text-destructive"
                                  title="Reject"
                                   onClick={async (e) => {
                                    e.stopPropagation();
                                    await supabase.from("profiles").update({ instagram_approval_status: "rejected" }).eq("user_id", p.user_id);
                                    toast({ title: "Rejected", description: "Instagram link rejected" });
                                    fetchData();
                                  }}
                                >
                                  <XCircle size={12} />
                                </Button>
                              </div>
                            ) : p.instagram_approval_status === "rejected" ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/20 text-destructive flex items-center gap-0.5">
                                <XCircle size={10} /> Rejected
                              </span>
                            ) : (
                              <a href={p.instagram_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary underline">View</a>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {/* Per-client bypass */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={p.instagram_bypass_approval}
                          onCheckedChange={async (checked) => {
                            await supabase.from("profiles").update({ instagram_bypass_approval: checked }).eq("user_id", p.user_id);
                            setProfiles((prev) => prev.map((pr) => pr.user_id === p.user_id ? { ...pr, instagram_bypass_approval: checked } : pr));
                            toast({ title: checked ? "Bypass enabled" : "Bypass disabled", description: `${p.full_name || "Client"} ${checked ? "can auto-approve Instagram links" : "needs admin approval"}` });
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(p.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {cancellationCount >= 2 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs gap-1"
                              onClick={() => handleReset(p.user_id)}
                            >
                              <RotateCcw size={12} />
                              Reset
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Client</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete <strong>{p.full_name || "this client"}</strong> and all their data. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteClient(p.user_id, p.full_name || "")}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Clients;
